import { TtOrderMetadata } from "@/lib/api/risk/brokerage/tt";

/**
 * Single source of truth for the TT order-type mapping.
 *
 * The backend persists the order type as five mutually-exclusive boolean
 * `expected_*` flags on `TtOrderAssociatedMetaData`. Everywhere in the UI we
 * want to think of those as one tagged union (`OrderType`). All conversions
 * go through this file so adding a new type only requires editing one place.
 */
export type OrderType =
  | "none"
  | "hta"
  | "pricing"
  | "position_adjust"
  | "cash"
  | "otc";

/** Display label used by chips, summaries, and form labels. */
export const ORDER_TYPE_LABELS: Record<Exclude<OrderType, "none">, string> = {
  hta: "HTA",
  pricing: "Pricing",
  position_adjust: "Position Adjust",
  cash: "Cash",
  otc: "OTC",
};

/** Persisted boolean field for each non-"none" order type. */
export const ORDER_TYPE_FLAG_KEY: Record<
  Exclude<OrderType, "none">,
  keyof Pick<
    TtOrderMetadata,
    | "expected_hta"
    | "expected_pricing"
    | "expected_position_adjust"
    | "expected_cash"
    | "expected_otc"
  >
> = {
  hta: "expected_hta",
  pricing: "expected_pricing",
  position_adjust: "expected_position_adjust",
  cash: "expected_cash",
  otc: "expected_otc",
};

/** Ordered list of non-"none" order types. Drives chip rendering. */
export const ORDER_TYPES: Array<Exclude<OrderType, "none">> = [
  "hta",
  "pricing",
  "position_adjust",
  "cash",
  "otc",
];

/** Read the current order type off persisted metadata. */
export function metadataToOrderType(meta: TtOrderMetadata | null): OrderType {
  if (!meta) return "none";
  for (const t of ORDER_TYPES) {
    if (meta[ORDER_TYPE_FLAG_KEY[t]]) return t;
  }
  return "none";
}

/** Build the `{ expected_*: boolean }` slice to send to the upsert API. */
export function orderTypeToExpectedFlags(orderType: OrderType): {
  expected_hta: boolean;
  expected_pricing: boolean;
  expected_position_adjust: boolean;
  expected_cash: boolean;
  expected_otc: boolean;
} {
  return {
    expected_hta: orderType === "hta",
    expected_pricing: orderType === "pricing",
    expected_position_adjust: orderType === "position_adjust",
    expected_cash: orderType === "cash",
    expected_otc: orderType === "otc",
  };
}

/** True when at least one expected_* flag is set. */
export function hasAnyOrderType(meta: TtOrderMetadata | null): boolean {
  return metadataToOrderType(meta) !== "none";
}
